import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes } from 'react-router-dom';
import Header from './components/Header';
import TrendingTagsBar from './components/TrendingTagsBar';
import Footer from './components/Footer';
import ProtectedRoute from './auth/ProtectedRoute';
import EmiCalculator from './calculators/EmiCalculator';
import LoanAmountCalculator from './calculators/LoanAmountCalculator';
import WhereYourEmiReallyGoesCalculator from './calculators/WhereYourEmiReallyGoesCalculator';
import InterestRateImpactCalculator from './calculators/InterestRateImpactCalculator';
import PreEmiCalculator from './calculators/PreEmiCalculator';
import TenureInterestCalculator from './calculators/TenureInterestCalculator';
import PartPrepaymentCalculator from './calculators/PartPrepaymentCalculator';
import HomeLoanAffordabilityCalculator from './calculators/HomeLoanAffordabilityCalculator';
import HomeLoanAffordabilityGuidedCalculator from './calculators/HomeLoanAffordabilityGuidedCalculator';
import ScrollToTop from './components/ScrollToTop';
import RouteSeo from './seo/RouteSeo';
import './App.css';
/* Listing + home card grids must load with main bundle — not only when lazy routes mount. */
import './pages/Home.css';
import './pages/CategoryPosts.css';
import './pages/TagPosts.css';

const Home = lazy(() => import('./pages/Home'));
const PostDetail = lazy(() => import('./pages/PostDetail'));
const CategoryPosts = lazy(() => import('./pages/CategoryPosts'));
const TagPosts = lazy(() => import('./pages/TagPosts'));
const SearchResults = lazy(() => import('./pages/SearchResults'));
const LatestInsights = lazy(() => import('./pages/LatestInsights'));
const FeaturedInsights = lazy(() => import('./pages/FeaturedInsights'));
const FinancialPlanningToolsPage = lazy(() => import('./pages/FinancialPlanningToolsPage'));
const About = lazy(() => import('./pages/About'));
const TermsAndConditions = lazy(() => import('./pages/TermsAndConditions'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Profile = lazy(() => import('./pages/Profile'));
const RetirementSavingsGoal = lazy(() => import('./calculators/RetirementSavingsGoal'));
const RetExpenseEstimator = lazy(() => import('./calculators/RetExpenseEstimator'));
const AreStocksLessRiskyChart = lazy(() => import('./components/charts/AreStocksLessRiskyChart'));
const SimulatedNiftyReturnsChart = lazy(() => import('./components/charts/SimulatedNiftyReturnsChart'));
const NpsVsUps = lazy(() => import('./calculators/NpsVsUps'));
const AccumulatedCorpus = lazy(() => import('./calculators/AccumulatedCorpus'));
const AppStartingEarly = lazy(() => import('./calculators/AppStartingEarly'));
const SavingsRequired = lazy(() => import('./calculators/SavingsRequired'));
const SipHistoricalAnalysis = lazy(() => import('./calculators/SipHistoricalAnalysis'));

function PageFallback() {
  return (
    <div className="loading">
      <div className="spinner" />
    </div>
  );
}

function App() {
  return (
    <Router>
      <ScrollToTop />
      <RouteSeo />
      <div className="app-shell">
        <Header />
        <TrendingTagsBar />
        <main className="main-content">
          <Routes>
            <Route
              path="/"
              element={(
                <Suspense fallback={<PageFallback />}>
                  <Home />
                </Suspense>
              )}
            />
            <Route
              path="/post/:slug"
              element={(
                <Suspense fallback={<PageFallback />}>
                  <PostDetail />
                </Suspense>
              )}
            />
            <Route
              path="/category/:slug"
              element={(
                <Suspense fallback={<PageFallback />}>
                  <CategoryPosts />
                </Suspense>
              )}
            />
            <Route
              path="/tag/:slug"
              element={(
                <Suspense fallback={<PageFallback />}>
                  <TagPosts />
                </Suspense>
              )}
            />
            <Route
              path="/search-results"
              element={(
                <Suspense fallback={<PageFallback />}>
                  <SearchResults />
                </Suspense>
              )}
            />
            <Route
              path="/latest-insights"
              element={(
                <Suspense fallback={<PageFallback />}>
                  <LatestInsights />
                </Suspense>
              )}
            />
            <Route
              path="/featured-insights"
              element={(
                <Suspense fallback={<PageFallback />}>
                  <FeaturedInsights />
                </Suspense>
              )}
            />
            <Route
              path="/tools"
              element={(
                <Suspense fallback={<PageFallback />}>
                  <FinancialPlanningToolsPage />
                </Suspense>
              )}
            />
            <Route
              path="/about"
              element={(
                <Suspense fallback={<PageFallback />}>
                  <About />
                </Suspense>
              )}
            />
            <Route
              path="/terms-and-conditions"
              element={(
                <Suspense fallback={<PageFallback />}>
                  <TermsAndConditions />
                </Suspense>
              )}
            />
            <Route
              path="/privacy-policy"
              element={(
                <Suspense fallback={<PageFallback />}>
                  <PrivacyPolicy />
                </Suspense>
              )}
            />
            <Route
              path="/login"
              element={(
                <Suspense fallback={<PageFallback />}>
                  <Login />
                </Suspense>
              )}
            />
            <Route
              path="/register"
              element={(
                <Suspense fallback={<PageFallback />}>
                  <Register />
                </Suspense>
              )}
            />
            <Route
              path="/forgot-password"
              element={(
                <Suspense fallback={<PageFallback />}>
                  <ForgotPassword />
                </Suspense>
              )}
            />
            <Route
              path="/reset-password/:token"
              element={(
                <Suspense fallback={<PageFallback />}>
                  <ResetPassword />
                </Suspense>
              )}
            />
            <Route
              path="/calculator/retirement-savings-goal"
              element={(
                <Suspense fallback={<PageFallback />}>
                  <RetirementSavingsGoal />
                </Suspense>
              )}
            />
            <Route
              path="/calculator/retirement-expense-estimator"
              element={(
                <Suspense fallback={<PageFallback />}>
                  <RetExpenseEstimator />
                </Suspense>
              )}
            />
            <Route
              path="/calculator/nps-vs-ups"
              element={(
                <Suspense fallback={<PageFallback />}>
                  <NpsVsUps />
                </Suspense>
              )}
            />
            <Route
              path="/calculator/savings-required"
              element={(
                <Suspense fallback={<PageFallback />}>
                  <SavingsRequired />
                </Suspense>
              )}
            />
            <Route
              path="/calculator/accumulated-corpus"
              element={(
                <Suspense fallback={<PageFallback />}>
                  <AccumulatedCorpus />
                </Suspense>
              )}
            />
            <Route
              path="/calculator/start-early-impact"
              element={(
                <Suspense fallback={<PageFallback />}>
                  <AppStartingEarly />
                </Suspense>
              )}
            />
            <Route
              path="/calculator/sip-historical-analysis"
              element={(
                <Suspense fallback={<PageFallback />}>
                  <SipHistoricalAnalysis />
                </Suspense>
              )}
            />
            <Route path="/calculator/emi-calculator" element={<EmiCalculator />} />
            <Route
              path="/calculator/loan-amount-calculator"
              element={<LoanAmountCalculator />}
            />
            <Route
              path="/calculator/where-your-emi-really-goes"
              element={<WhereYourEmiReallyGoesCalculator />}
            />
            <Route
              path="/calculator/interest-rate-impact"
              element={<InterestRateImpactCalculator />}
            />
            <Route path="/calculator/pre-emi-calculator" element={<PreEmiCalculator />} />
            <Route
              path="/calculator/tenure-interest-calculator"
              element={<TenureInterestCalculator />}
            />
            <Route
              path="/calculator/part-prepayment-calculator"
              element={<PartPrepaymentCalculator />}
            />
            <Route
              path="/calculator/home-loan-affordability"
              element={<HomeLoanAffordabilityCalculator />}
            />
            <Route
              path="/calculator/home-loan-affordability-guided"
              element={<HomeLoanAffordabilityGuidedCalculator />}
            />
            <Route
              path="/chart/are-stocks-less-risky"
              element={(
                <Suspense fallback={<PageFallback />}>
                  <div className="container" style={{ padding: '2rem 20px' }}>
                    <AreStocksLessRiskyChart variant="full" />
                  </div>
                </Suspense>
              )}
            />
            <Route
              path="/chart/simulated-nifty-returns"
              element={(
                <Suspense fallback={<PageFallback />}>
                  <div className="container" style={{ padding: '2rem 20px' }}>
                    <SimulatedNiftyReturnsChart variant="full" />
                  </div>
                </Suspense>
              )}
            />
            <Route
              path="/profile"
              element={(
                <Suspense fallback={<PageFallback />}>
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                </Suspense>
              )}
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
        <Footer />
      </div>
    </Router>
  );
}

export default App;
